import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchApprovedParentChildren } from '../api/guardian';
import { getActiveRole } from '../utils/auth';

const STORAGE_KEY = 'parentSelectedStudentId';

const ParentChildContext = createContext(null);

export const ParentChildProvider = ({ children }) => {
    const role = getActiveRole();
    const enabled = role === 'PARENT';
    const [approvedChildren, setApprovedChildren] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [loadError, setLoadError] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState(() => {
        if (!enabled) return null;
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? Number(raw) : null;
    });

    const refresh = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        setLoadError(false);
        try {
            const list = await fetchApprovedParentChildren();
            const approved = Array.isArray(list) ? list.filter((c) => c.status === 'APPROVED') : [];
            setApprovedChildren(approved);
            if (selectedStudentId && !approved.some((c) => c.studentId === selectedStudentId)) {
                setSelectedStudentId(null);
                sessionStorage.removeItem(STORAGE_KEY);
            }
            if (!selectedStudentId && approved.length === 1) {
                setSelectedStudentId(approved[0].studentId);
                sessionStorage.setItem(STORAGE_KEY, String(approved[0].studentId));
            }
        } catch {
            setApprovedChildren([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [enabled, selectedStudentId]);

    useEffect(() => {
        if (enabled) refresh();
    }, [enabled, refresh]);

    const selectChild = useCallback((studentId) => {
        setSelectedStudentId(studentId);
        if (studentId != null) {
            sessionStorage.setItem(STORAGE_KEY, String(studentId));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const selectedChild = useMemo(
        () => approvedChildren.find((c) => c.studentId === selectedStudentId) || null,
        [approvedChildren, selectedStudentId],
    );

    const value = useMemo(
        () => ({
            enabled,
            loading,
            loadError,
            approvedChildren,
            selectedStudentId,
            selectedChild,
            selectChild,
            refresh,
            childStudentId: selectedStudentId,
        }),
        [enabled, loading, loadError, approvedChildren, selectedStudentId, selectedChild, selectChild, refresh],
    );

    return (
        <ParentChildContext.Provider value={value}>
            {children}
        </ParentChildContext.Provider>
    );
};

export const useParentChild = () => {
    const ctx = useContext(ParentChildContext);
    if (!ctx) {
        return {
            enabled: false,
            loading: false,
            loadError: false,
            approvedChildren: [],
            selectedStudentId: null,
            selectedChild: null,
            childStudentId: null,
            selectChild: () => {},
            refresh: () => {},
        };
    }
    return ctx;
};

export default ParentChildContext;
